import { formatNumber } from 'util/formatNumber';
import { Box, Typography } from '@mui/material';
import { ArrowForwardIosRounded } from '@mui/icons-material';
import { OrganizationsIcon, CalendarIcon, TicketIcon } from 'components/icons';
import { Link, Image, TruncateText } from 'components/lib';
import {
  StatDisplay,
  AddLink,
  TextInput,
  GridTable,
  GridTableHead,
  Button,
} from 'components/ui';
import {
  useRequireAuth,
  useInfiniteScroll,
  useFileSelector,
  useInfiniteLoader,
} from 'hooks';
import { debounce } from 'lodash';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { useSchoolsApi } from 'services/api';
import { Routes } from 'services/routes';
import useSWR from 'swr';
import { useMasterApi } from 'services/api/hooks/useMasterApi.hook';
import { useSnackbar } from 'notistack';
import { theme } from 'theme';

export interface OrganizationsTabProps {
  schoolId: string;
}

export const OrganizationsTab = ({
  schoolId,
}: OrganizationsTabProps): ReactElement => {
  useRequireAuth();

  const schoolsApi = useSchoolsApi();

  const rSchoolStats = useSWR(
    `/schools/${schoolId}/stats`,
    async () => {
      if (!schoolId) return;

      const result = await schoolsApi.getStats(schoolId);

      if (result.success) return result.value;

      if (result.error.response?.status === 401) return;

      throw new Error();
    },
    {
      revalidateOnFocus: false,
      refreshInterval: 0,
    },
  );

  const [search, setSearch] = useState<string | null>(null);

  const masterApi = useMasterApi();

  const listOrganizations = useCallback(
    (...args: Parameters<typeof masterApi.listSchoolOrganizations>) =>
      masterApi.listSchoolOrganizations(...args),
    [],
  );

  const rOrganizations = useInfiniteLoader(
    (pageIndex, previousPageData) =>
      [search, previousPageData?.cursor] as const,
    (search: string | null, cursor?: string | null) => {
      return listOrganizations(schoolId, {
        search,
        cursor,
      });
    },
  );

  useInfiniteScroll(() => {
    if (
      !rOrganizations.isValidating &&
      rOrganizations.data?.length === rOrganizations.size
    )
      void rOrganizations.setSize(rOrganizations.size + 1);
  });

  const handleSearchChange = useCallback(
    debounce((search: string) => {
      setSearch(search);
    }, 200),
    [],
  );

  const { enqueueSnackbar } = useSnackbar();

  const { open } = useFileSelector({
    accept:
      '.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    multiple: false,
    onChange: useCallback(async files => {
      if (!files || files.length < 1) return;

      const file = files.item(0) as File;

      const result = await masterApi.importOrganizations(schoolId, file);

      if (result.success) {
        await rOrganizations.mutate();
        enqueueSnackbar('Organizations successfully imported', {
          variant: 'success',
        });
        return;
      }

      enqueueSnackbar(result.error.message, { variant: 'error' });
    }, []),
  });

  const organizations =
    rOrganizations.data?.flatMap(page => page?.organizations ?? []) ?? [];

  const organizationsTable = useMemo(() => {
    if (!organizations.length) return;

    return (
      <>
        <Box display={{ xs: 'block', md: 'none' }}>
          {organizations.map((org, i) => (
            <React.Fragment key={org.id}>
              <Link href={Routes.admin.viewOrganization(org.id)}>
                <Box
                  mt={i === 0 ? 20 : 30}
                  display="flex"
                  alignItems="center"
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  <Box mr={10.5} lineHeight={0}>
                    <Image
                      src={org.logoUrl}
                      alt={org.name}
                      width={30}
                      height={30}
                    />
                  </Box>
                  <Box mr={15}>
                    <Typography
                      color="primary"
                      style={{
                        fontWeight: 600,
                        lineHeight: '1.2em',
                        height: '2.4em',
                        overflow: 'hidden',
                      }}
                    >
                      {org.name}
                    </Typography>
                  </Box>
                  <Box ml="auto">
                    <ArrowForwardIosRounded
                      color="primary"
                      style={{ fontSize: '0.825em' }}
                    />
                  </Box>
                </Box>
              </Link>
            </React.Fragment>
          ))}
        </Box>
        <Box display={{ xs: 'none', md: 'block' }}>
          <GridTable
            gridTemplateRows="[head] 50px"
            gridTemplateColumns="4fr 2fr 2fr 2fr"
            gridAutoRows="1fr"
            justifyItems="center"
            alignItems="center"
            style={{
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <GridTableHead>Organization Name</GridTableHead>
            <GridTableHead textAlign="center">Pending Events</GridTableHead>
            <GridTableHead textAlign="center">Live Events</GridTableHead>
            <GridTableHead textAlign="center">Past Events</GridTableHead>

            {organizations.map(org => (
              <React.Fragment key={org.id}>
                <Box width="100%">
                  <Link href={Routes.admin.viewOrganization(org.id)}>
                    <Box
                      width="100%"
                      display="flex"
                      alignItems="center"
                      style={{
                        cursor: 'pointer',
                      }}
                    >
                      <Box mr={10.5} py={20} lineHeight={0}>
                        <Image
                          src={org.logoUrl}
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                          alt={org.name}
                          width={30}
                          height={30}
                        />
                      </Box>
                      <Box flex={1} mr={15} style={{ verticalAlign: 'middle' }}>
                        <TruncateText lines={2}>
                          <Typography
                            color="primary"
                            style={{
                              fontFamily: "'Poppins', 'Sans Serif'",
                              fontWeight: 600,
                              lineHeight: '1.2em',
                              maxHeight: '2.4em',
                              overflow: 'hidden',
                            }}
                          >
                            {org.name}
                          </Typography>
                        </TruncateText>
                      </Box>
                    </Box>
                  </Link>
                </Box>
                <Typography>{formatNumber(org.numEvents.pending)}</Typography>
                <Typography>{formatNumber(org.numEvents.live)}</Typography>
                <Typography>{formatNumber(org.numEvents.past)}</Typography>
              </React.Fragment>
            ))}
          </GridTable>
        </Box>
      </>
    );
  }, [rOrganizations.data]);

  return (
    <Box maxWidth={880}>
      <Box
        mt={20}
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Box mt={{ xs: 30, md: 0 }}>
          <AddLink href={Routes.admin.createOrganization()}>
            Add New Organization
          </AddLink>
        </Box>

        <Box mt={{ xs: 30, md: 0 }}>
          <Typography sx={{ fontWeight: 600 }}>Upload Organizations</Typography>
          <Button
            onClick={open}
            style={{
              padding: '14px 50px',
              background: theme.palette.neutral.main,
              border: 'none',
            }}
            inverted
          >
            Click Here To Upload File
          </Button>
        </Box>

        <Box width={300} mt={{ xs: 30, md: 0 }}>
          <TextInput
            label="Filter Organizations"
            placeholder="Find organization by name"
            onChange={e => handleSearchChange(e.target.value)}
          />
        </Box>
      </Box>

      <Box mt={30} display="flex">
        <Box flex={1} mr={30}>
          <StatDisplay
            icon={<OrganizationsIcon />}
            label="Organizations"
            value={rSchoolStats.data?.organizations}
          />
        </Box>
        <Box flex={1} mr={30}>
          <StatDisplay
            icon={<CalendarIcon />}
            label="Events"
            value={rSchoolStats.data?.events}
          />
        </Box>
        <Box flex={1}>
          <StatDisplay
            icon={<TicketIcon />}
            label="Tickets Reserved"
            value={rSchoolStats.data?.tickets}
          />
        </Box>
      </Box>

      <Box mt={20}>{organizationsTable}</Box>
    </Box>
  );
};
